import { Injectable, computed, inject } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';
import { type Observable, catchError, forkJoin, from, of, take, tap } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import type { Address } from 'viem';

import { WalletService } from '../../core/services/wallet';
import { CUSTOS_ENVIRONMENT, type CustosEnvironment } from '../../core/models/environment';
import { API_SERVICE, type CustosApi } from '../../core/api/api.provider';
import { MarketCacheService } from '../../core/services/market-cache.service';
import type { ApiLocker } from '../../core/api/api.types';
import { ERC20_ABI } from '../../core/contracts/abis/erc20.abi';
import { PREDICT_MARKET_ABI } from '../../core/contracts/abis/predict-market.abi';
import { TOKEN_LOCKER_ABI } from '../../core/contracts/abis/token-locker.abi';
import { weiToHuman, truncateWeiToCents } from '../../core/data/data-transform';

interface WalletStoreState {
  // Per-token wallet balances (key = lowercase token address).
  // Display value is truncated to 2 decimals; wei stores the exact on-chain
  // amount so the bet-submit path can use BigInt all the way and MAX never
  // exceeds the real balance.
  tokenBalances: Record<string, number>;
  tokenBalancesWei: Record<string, bigint>;
  tokenBalancesFetchedAt: Record<string, number>;

  // Per-locker locked balances (key = lowercase locker contract address).
  // Same dual-store pattern as tokenBalances. The endTime/decimals maps
  // let the spendable-amount selectors re-derive at read time so a
  // pre-expiry cached balance flips to 0 once endTime passes without
  // needing a refetch (Wesley 2026-05-25 + Codex stale-state catch).
  lockerBalances: Record<string, number>;
  lockerBalancesWei: Record<string, bigint>;
  lockerEndTimes: Record<string, number>;
  lockerDecimals: Record<string, number>;
  lockerBalancesFetchedAt: Record<string, number>;

  loadingCount: number;
  error: string | null;
}

const INITIAL_STATE: WalletStoreState = {
  tokenBalances: {},
  tokenBalancesWei: {},
  tokenBalancesFetchedAt: {},
  lockerBalances: {},
  lockerBalancesWei: {},
  lockerEndTimes: {},
  lockerDecimals: {},
  lockerBalancesFetchedAt: {},
  loadingCount: 0,
  error: null,
};

const CACHE_DURATION_MS = 30_000;

@Injectable({ providedIn: 'root' })
export class WalletStore {
  private readonly wallet = inject(WalletService);
  private readonly env = inject<CustosEnvironment>(CUSTOS_ENVIRONMENT);
  private readonly api = inject<CustosApi>(API_SERVICE);
  private readonly marketCache = inject(MarketCacheService);
  private readonly state = signalState<WalletStoreState>(INITIAL_STATE);

  /** Dynamically discovered base token when ctsToken is not configured. */
  private discoveredBaseToken: { address: Address; decimals: number } | null = null;

  /** Call after wallet connects or after transactions to refresh balances. */
  refreshAll(): void {
    if (this.walletAddress) {
      this.loadWalletBalance(true);
      this.loadRollockerBalance(true);
    } else {
      this.clear();
    }
  }

  private get walletAddress(): Address | null {
    return this.wallet.address();
  }

  private readonly ctsTokenAddress = (this.env.contracts.ctsToken as string).toLowerCase();

  private get lockerAddress(): Address {
    return this.env.contracts.rollocker as Address;
  }

  // ==========================================================
  // State Selectors
  // ==========================================================

  readonly isLoading = computed(() => this.state().loadingCount > 0);
  readonly error = computed(() => this.state().error);

  // walletBalance() / rollockerBalance() / totalBalance / hasBalance —
  // removed 2026-04-23. Were implicit-context getters that resolved to
  // whatever the last render had populated; different pages saw different
  // values for nominally the same call. Zero external callsites by the
  // time of removal. Use `balanceForToken(addr)` /
  // `predictionStore.lockerBalanceForMarket(marketId)` instead.

  // ==========================================================
  // Per-token balance access
  // ==========================================================

  /** Get balance for a token (returns null if not loaded). */
  balanceForToken(tokenAddress: string): number | null {
    const key = tokenAddress.toLowerCase();
    return this.state().tokenBalances[key] ?? null;
  }

  /** Whether a balanceOf read has completed AND is still fresh (within
   *  CACHE_DURATION_MS). Distinct from "balance is zero" — used by
   *  amount-input's MAX button to avoid submitting either 0 during the
   *  per-dialog discover race OR a stale BigInt that no longer matches
   *  the user's on-chain balance (e.g. they transferred out in another tab). */
  isTokenBalanceLoaded(tokenAddress: string): boolean {
    const key = tokenAddress.toLowerCase();
    const fetchedAt = this.state().tokenBalancesFetchedAt[key];
    return fetchedAt != null && Date.now() - fetchedAt < CACHE_DURATION_MS;
  }

  /** Exact on-chain wei balance for a token, for submitting as amountWei. */
  balanceWeiForToken(tokenAddress: string): bigint | null {
    const key = tokenAddress.toLowerCase();
    return this.state().tokenBalancesWei[key] ?? null;
  }

  /** Exact on-chain wei balance for a locker contract address.
   *  Returns 0n once the locker is expired, even if the original read
   *  happened pre-expiry — derived at call time (Wesley 2026-05-25). */
  balanceWeiForLocker(lockerAddress: string): bigint | null {
    const key = lockerAddress.toLowerCase();
    const wei = this.state().lockerBalancesWei[key];
    if (wei == null) return null;
    const endTime = this.state().lockerEndTimes[key] ?? 0;
    if (endTime > 0 && Date.now() / 1000 > endTime) return 0n;
    return wei;
  }

  /** Human-display balance for a locker contract (staking amount, truncated).
   *  Same expiry-aware derivation as balanceWeiForLocker. */
  balanceForLocker(lockerAddress: string): number | null {
    const key = lockerAddress.toLowerCase();
    const display = this.state().lockerBalances[key];
    if (display == null) return null;
    const endTime = this.state().lockerEndTimes[key] ?? 0;
    if (endTime > 0 && Date.now() / 1000 > endTime) return 0;
    return display;
  }

  // ==========================================================
  // Private helpers
  // ==========================================================

  private startLoading(): void {
    patchState(this.state, { loadingCount: this.state().loadingCount + 1 });
  }

  private stopLoading(): void {
    patchState(this.state, { loadingCount: Math.max(0, this.state().loadingCount - 1) });
  }

  private isTokenCacheValid(tokenAddress: string): boolean {
    const fetchedAt = this.state().tokenBalancesFetchedAt[tokenAddress.toLowerCase()];
    return fetchedAt !== undefined && Date.now() - fetchedAt < CACHE_DURATION_MS;
  }

  private isLockerCacheValid(): boolean {
    // Locker cache is valid if ANY locker entry exists and the oldest one is within the window.
    // Simpler: use a single sentinel key for the batch fetch.
    const fetchedAt = this.state().lockerBalancesFetchedAt['__batch__'];
    return fetchedAt !== undefined && Date.now() - fetchedAt < CACHE_DURATION_MS;
  }

  // ==========================================================
  // Load Methods
  // ==========================================================

  /** Load balance for a specific ERC-20 token. */
  loadTokenBalance(tokenAddress: Address, decimals: number, forceRefresh = false): void {
    const key = (tokenAddress as string).toLowerCase();
    if (!forceRefresh && this.isTokenCacheValid(key)) return;

    const addr = this.walletAddress;
    if (!addr) return;

    this.startLoading();
    patchState(this.state, { error: null });

    const client = this.wallet.getPublicClient();
    from(
      client.readContract({
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: 'balanceOf',
        args: [addr],
      }) as Promise<bigint>,
    ).pipe(
      // Keep both shapes: truncated Number for display + exact wei for submit.
      map((wei) => ({ wei, display: truncateWeiToCents(wei, decimals) })),
      take(1),
      tap(({ wei, display }) => {
        this.stopLoading();
        patchState(this.state, {
          tokenBalances: { ...this.state().tokenBalances, [key]: display },
          tokenBalancesWei: { ...this.state().tokenBalancesWei, [key]: wei },
          tokenBalancesFetchedAt: { ...this.state().tokenBalancesFetchedAt, [key]: Date.now() },
        });
      }),
      catchError((err: Error) => {
        console.error(`[WalletStore] Failed to load balance for ${tokenAddress}:`, err);
        this.stopLoading();
        patchState(this.state, {
          error: err.message || 'Failed to load wallet balance',
        });
        return of(null);
      }),
    ).subscribe();
  }

  /** Load CTS token balance — backward compat wrapper. */
  loadWalletBalance(forceRefresh = false): void {
    const ctsToken = this.env.contracts.ctsToken;
    if (ctsToken) {
      this.loadTokenBalance(ctsToken as Address, 18, forceRefresh);
      return;
    }

    // Use cached discovered token if available
    if (this.discoveredBaseToken) {
      this.loadTokenBalance(this.discoveredBaseToken.address, this.discoveredBaseToken.decimals, forceRefresh);
      return;
    }

    // Discover base token from first market's prediction contract
    this.marketCache.getMarkets().pipe(take(1)).subscribe((markets) => {
      if (markets.length === 0) return;
      const contract = markets[0].prediction_contract as Address;
      const client = this.wallet.getPublicClient();

      client.readContract({
        address: contract,
        abi: PREDICT_MARKET_ABI,
        functionName: 'baseToken',
      }).then(async (tokenAddr) => {
        const decimals = await client.readContract({
          address: tokenAddr as Address,
          abi: ERC20_ABI,
          functionName: 'decimals',
        });
        this.discoveredBaseToken = { address: tokenAddr as Address, decimals: Number(decimals) };
        this.loadTokenBalance(this.discoveredBaseToken.address, this.discoveredBaseToken.decimals, forceRefresh);
      }).catch((err: unknown) => {
        console.error('[WalletStore] Failed to discover base token:', err);
      });
    });
  }

  loadRollockerBalance(forceRefresh = false): void {
    if (!forceRefresh && this.isLockerCacheValid()) return;

    const addr = this.walletAddress;
    if (!addr) return;

    this.startLoading();
    patchState(this.state, { error: null });

    const client = this.wallet.getPublicClient();

    // Fetch every locker and read its raw allLocks[0] — that's the
    // Staking Amount (wei still in the locker, spendable for new bets).
    // Active prediction orders are intentionally NOT summed in — they're
    // already committed and would inflate the "available" figure the UI
    // offers to MAX (Wesley 2026-04-21).
    this.api.getLockers({ chain_id: this.env.chainId }).pipe(
      catchError(() => of([{
        locker_contract: this.lockerAddress,
        base_token_decimals: 18,
        locker_id: '',
      } as ApiLocker])),
      switchMap((lockers) => {
        if (lockers.length === 0) {
          return of({
            lockerBalances: {} as Record<string, number>,
            lockerBalancesWei: {} as Record<string, bigint>,
            lockerEndTimes: {} as Record<string, number>,
            lockerDecimals: {} as Record<string, number>,
          });
        }

        const lockRequests = lockers.map((locker) => {
          const lockerKey = (locker.locker_contract as string).toLowerCase();
          const decimals = locker.base_token_decimals ?? 18;

          // Read raw stake — the tokens STILL sitting in the locker, i.e.
          // the Staking Amount that is available to spend on a new bet.
          // Do NOT add in amounts already committed to active predictions
          // (Wesley 2026-04-21: Rollocker Balance = Staking, not Total —
          // otherwise MAX lets users bet funds they've already locked).
          return from(
            client.readContract({
              address: locker.locker_contract as Address,
              abi: TOKEN_LOCKER_ABI,
              functionName: 'allLocks',
              args: [addr],
            }) as Promise<readonly [bigint, number, number]>,
          ).pipe(
            map((info) => ({
              wei: info[0],
              display: truncateWeiToCents(info[0], decimals),
              endTime: Number(info[1]),
              decimals,
            })),
            catchError(() => of({ wei: 0n as bigint, display: 0, endTime: 0, decimals })),
            map((r) => ({ key: lockerKey, balance: r.display, wei: r.wei, endTime: r.endTime, decimals: r.decimals })),
            catchError(() => of({ key: lockerKey, balance: 0, wei: 0n as bigint, endTime: 0, decimals })),
          );
        });

        return forkJoin(lockRequests).pipe(
          map((results) => {
            const lockerBalances: Record<string, number> = {};
            const lockerBalancesWei: Record<string, bigint> = {};
            const lockerEndTimes: Record<string, number> = {};
            const lockerDecimals: Record<string, number> = {};
            for (const r of results) {
              lockerBalances[r.key] = r.balance;
              lockerBalancesWei[r.key] = r.wei;
              lockerEndTimes[r.key] = r.endTime;
              lockerDecimals[r.key] = r.decimals;
            }
            return { lockerBalances, lockerBalancesWei, lockerEndTimes, lockerDecimals };
          }),
        );
      }),
      take(1),
      tap(({ lockerBalances, lockerBalancesWei, lockerEndTimes, lockerDecimals }) => {
        this.stopLoading();
        const now = Date.now();
        const fetchedAt: Record<string, number> = { '__batch__': now };
        for (const key of Object.keys(lockerBalances)) {
          fetchedAt[key] = now;
        }
        patchState(this.state, {
          lockerEndTimes: { ...this.state().lockerEndTimes, ...lockerEndTimes },
          lockerDecimals: { ...this.state().lockerDecimals, ...lockerDecimals },
          lockerBalances,
          lockerBalancesWei,
          lockerBalancesFetchedAt: fetchedAt,
        });
      }),
      catchError((err: Error) => {
        console.error('[WalletStore] Failed to load rollocker balance:', err);
        this.stopLoading();
        patchState(this.state, {
          error: err.message || 'Failed to load rollocker balance',
        });
        return of(null);
      }),
    ).subscribe();
  }

  // ==========================================================
  // Token Balance (any ERC-20)
  // ==========================================================

  /**
   * Read `balanceOf` for any ERC-20 token.
   * Returns the human-readable balance as an Observable.
   */
  getTokenBalance(tokenAddress: Address, decimals = 18): Observable<number> {
    const addr = this.walletAddress;
    if (!addr) return of(0);

    const client = this.wallet.getPublicClient();
    return from(
      client.readContract({
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: 'balanceOf',
        args: [addr],
      }) as Promise<bigint>,
    ).pipe(
      map((balance) => weiToHuman(balance, decimals)),
      take(1),
      catchError((err: Error) => {
        console.error(`[WalletStore] Failed to read balance for ${tokenAddress}:`, err);
        return of(0);
      }),
    );
  }

  // ==========================================================
  // Utility
  // ==========================================================

  clear(): void {
    patchState(this.state, INITIAL_STATE);
  }
}
